import { FC, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import LmsEnrolmentsTable from "@/components/lms/lmsEnrolmentsTable";
import LmsEnrolmentCreateModal from "@/components/lms/lmsEnrolmentCreateModal";
import { RootState } from "@/components/state/store";
import {
  COURSE_ADMIN_PERMISSION,
  getEffectiveLmsMode,
  getLmsEnrolmentsRoute,
} from "@/components/lms/lms.utils";
import styles from "@/components/subjectOverview/subjectOverviewEnrolments.module.css";

interface Props {
  detailedSubject?: { id?: string; name?: string };
}

const SubjectOverviewEnrolments: FC<Props> = ({ detailedSubject }) => {
  const { t } = useTranslation();
  const loginState = useSelector((state: RootState) => state.login);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { handleError, errorModal } = useErrorModal();
  const [notificationMessage, setNotificationMessage] = useState("");

  const subdomain = window.location.hostname.split(".")[0];
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });

  const participantId = detailedSubject?.id;
  const presetParticipants = useMemo(
    () =>
      participantId
        ? [
            {
              value: String(participantId),
              label: detailedSubject?.name ?? String(participantId),
            },
          ]
        : [],
    [detailedSubject?.name, participantId]
  );

  const handleRefresh = () => {
    setRefreshKey((currentValue) => currentValue + 1);
  };

  const handleCreateCompleted = () => {
    setIsCreateModalOpen(false);
    handleRefresh();
    setNotificationMessage(t("users_enrolled_successfully"));
  };

  const customTopToolbarAction = () => (
    <div className={styles.toolbarActions}>
      <GenericButton
        startIcon={<AddIcon />}
        onClick={() => setIsCreateModalOpen(true)}
      >
        {t("add")}
      </GenericButton>
      <GenericButton startIcon={<OpenInNewIcon />}>
        <Link
          to={getLmsEnrolmentsRoute(effectiveMode)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link_white}
        >
          {t("enrolments")}
        </Link>
      </GenericButton>
    </div>
  );

  if (
    !participantId ||
    !loginState.permissions.includes(COURSE_ADMIN_PERMISSION)
  ) {
    return null;
  }

  return (
    <>
      <LmsEnrolmentsTable
        mode={effectiveMode}
        subjectOverviewMode
        participantId={String(participantId)}
        includeCourseColumn
        customTopToolBarAction={customTopToolbarAction}
        refreshKey={refreshKey}
        onRefreshed={handleRefresh}
        onError={handleError}
      />

      <LmsEnrolmentCreateModal
        open={isCreateModalOpen}
        mode={effectiveMode}
        onClose={() => setIsCreateModalOpen(false)}
        onCompleted={handleCreateCompleted}
        enableCourseSelection={true}
        presetParticipants={presetParticipants}
        lockParticipantSelection={true}
      />

      {notificationMessage ? (
        <GenericNotification
          message={notificationMessage}
          variant="success"
          handleCloseSnackbar={() => setNotificationMessage("")}
        />
      ) : null}

      {errorModal}
    </>
  );
};

export default SubjectOverviewEnrolments;
